import { useCallback } from 'react';
import {
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from 'react-query';
import { useClearCreditCache } from './credits';
import { API_URL, queryCreate, queryGet, queryUpdate } from './fetch';
import * as Stripe from '@stripe/stripe-js';

export enum InvoiceStatus {
  DRAFT = 'DRAFT',
  OPEN = 'OPEN',
  PAID = 'PAID',
  VOID = 'VOID',
  UNCOLLECTIBLE = 'UNCOLLECTIBLE',
  REFUNDED = 'REFUNDED',
  ISSUED = 'ISSUED',
}

export interface InvoiceProductLine {
  id: string;
  title?: string;
  type?: string;
  unitPrice?: number;
  quantity: number;
  total?: number;
  credits?: number;
  productId?: string;
  invoiceId: string;
}

export interface Invoice {
  id: string;
  sessionId?: string;
  organizationId?: string;
  name?: string;
  stripeId?: string;
  provider?: InvoiceProvider;
  total?: number;
  tax?: number;
  totalHT?: number;
  status: InvoiceStatus;
  creditNoteId?: string;
  openAt?: string;
  generatedAt?: string;
  paidAt?: string;
  dueDate?: string;
  purchaseOrder?: string;
  receiptUrl?: string;
  productLines?: InvoiceProductLine[];
  createdAt: string;

  studyTotal?: number;
  optionsTotal?: number;
  incentiveTotal?: number;
  feesTotal?: number;
  discountTotal?: number;
  subscriptionTotal?: number;
  creditsTotal?: number;
  supportTotal?: number;
  creditUsageTotal?: number;
  serviceTotal?: number;
  bonusCredits?: number;
}

export const useInvoice = (
  invoiceId?: string,
  options?: Partial<UseQueryOptions<Invoice>>,
) => {
  return useQuery<Invoice>(
    ['invoices', invoiceId],
    queryGet(`/v2/invoices/${invoiceId}`),
    {
      enabled: !!invoiceId,
      ...options,
    },
  );
};

export enum InvoiceDocumentType {
  INVOICE = 'INVOICE',
  CREDIT_NOTE = 'CREDIT_NOTE',
  PDF = 'PDF',
}

enum InvoiceProvider {
  STRIPE = 'STRIPE',
  CEGID = 'CEGID',
  SEVDESK = 'SEVDESK',
}

export const isStripeInvoice = (invoice?: Invoice) => {
  if (!invoice) {
    return false;
  }

  return /^(in)|(cn)_/.test(invoice.id);
};

export const getInvoiceUrl = (
  invoice?: Invoice,
  doc: InvoiceDocumentType = InvoiceDocumentType.INVOICE,
) => {
  if (!invoice) {
    return undefined;
  }

  if (invoice.status === InvoiceStatus.DRAFT) {
    if (isStripeInvoice(invoice)) {
      return undefined;
    }
  }

  return `${API_URL}/v2/view-invoice/${invoice.id}/${doc}`;
};

const APP_ENV = process.env.NX_APP_ENV;

const CEGID_COMPANY_ID =
  APP_ENV === 'prod' || APP_ENV === 'production'
    ? 'BILENDI_FRANCE'
    : 'R7_BILENDI+TECHNOLOGY+LAB';

const DASHBOARD_URL: Record<InvoiceProvider, (invoice: Invoice) => string> = {
  STRIPE: ({ id }) =>
    `https://dashboard.stripe.com/invoices/${encodeURIComponent(id)}`,
  CEGID: ({ name }) =>
    `https://xrp-flex.cegid.cloud/bilendisa/(W(19))/Main?CompanyID=${CEGID_COMPANY_ID}&ScreenId=AR301000&DocType=INV&RefNbr=${encodeURIComponent(
      name!,
    )}`,
  SEVDESK: ({ name }) =>
    `https://my.sevdesk.de/fi/edit/type/RE/id/${encodeURIComponent(name!)}`,
};

export const getDashboardUrl = (invoice?: Invoice): string | undefined => {
  if (!invoice?.provider) return undefined;
  return DASHBOARD_URL[invoice.provider]?.(invoice);
};

export const useInvoices = (organizationId?: string) => {
  const queryClient = useQueryClient();
  return useQuery<Invoice[]>(
    ['organizations', organizationId, 'invoices'],
    queryGet(`/v2/organizations/${organizationId}/invoices`),
    {
      enabled: !!organizationId,
      onSuccess: (invoices) => {
        for (const invoice of invoices) {
          queryClient.setQueryData(['invoices', invoice.id], invoice);
        }
      },
    },
  );
};

export const useSessionInvoices = (
  sessionId?: string,
  options?: Partial<UseQueryOptions<Invoice[]>>,
) => {
  const queryClient = useQueryClient();
  return useQuery<Invoice[]>(
    ['sessions', sessionId, 'invoices'],
    queryGet(`/v2/sessions/${sessionId}/invoices`),
    {
      enabled: !!sessionId,
      onSuccess: (invoices) => {
        for (const invoice of invoices) {
          queryClient.setQueryData(['invoices', invoice.id], invoice);
        }
      },
      ...options,
    },
  );
};

interface GenerateInvoiceParams {
  organizationId: string;
  purchaseOrder?: string;
}

export const useGenerateInvoiceForOffer = (offerId?: string) => {
  const clearCreditCache = useClearCreditCache();
  return useMutation<Invoice, Error, GenerateInvoiceParams>(
    queryCreate(`/v2/offers/${offerId}/invoices`),
    {
      onSuccess: clearCreditCache,
    },
  );
};

interface GenerateInvoiceOptions {
  // @deprecated will be removed when moved to Cegid/sevdesk
  metadata?: Record<string, string>;
  purchaseOrder?: string;
  invoiceDate?: string;
}

export const useGenerateInvoiceForSession = (
  sessionId?: string,
  organizationId?: string,
  options: UseMutationOptions<Invoice, Error, GenerateInvoiceOptions> = {},
) => {
  const clearCreditCache = useClearCreditCache();
  const queryClient = useQueryClient();

  const onMutate = useCallback(
    (opts: GenerateInvoiceOptions) => {
      setTimeout(() => {
        queryClient.invalidateQueries(['sessions', sessionId, 'orders']);
        queryClient.invalidateQueries(['sessions', sessionId, 'invoices']);
      }, 1000);
      if (options.onMutate) {
        options.onMutate(opts);
      }
    },
    [options],
  );

  const onSuccess = useCallback(
    (data: Invoice, variables: GenerateInvoiceOptions, context: unknown) => {
      queryClient.invalidateQueries(['sessions', sessionId, 'orders']);
      queryClient.invalidateQueries(['sessions', sessionId, 'invoices']);
      clearCreditCache();
      if (organizationId) {
        queryClient.invalidateQueries([
          'organizations',
          organizationId,
          'sessions',
          sessionId,
          'product-lines',
        ]);
      }
      if (options.onSuccess) {
        options.onSuccess(data, variables, context);
      }
    },
    [queryClient, clearCreditCache, sessionId, organizationId, options],
  );

  return useMutation<Invoice, Error, GenerateInvoiceOptions>(
    queryCreate(`/v2/sessions/${sessionId}/invoices`),
    {
      ...options,
      onSuccess,
      onMutate,
    },
  );
};

export const useFinalizeInvoice = (id?: string) => {
  return useMutation<
    Invoice,
    Error,
    { shouldSendEmail?: boolean; extraBillingEmails?: string[] }
  >(queryUpdate(`/v2/invoices/${id}/finalize`));
};

export const useCreateInvoicePaymentIntent = (invoiceId?: string) => {
  return useMutation<Stripe.PaymentIntent, Error, void>(
    queryCreate(`/v2/invoices/${invoiceId}/payment-intent`),
  );
};

export const useConfirmInvoicePayment = (invoiceId?: string) => {
  const queryClient = useQueryClient();
  return useMutation<Invoice, Error, { intentId: string }>(
    queryCreate(`/v2/invoices/${invoiceId}/confirm-payment`),
    {
      onSuccess: (invoice) => {
        queryClient.setQueryData(['invoices', invoice.id], invoice);
      },
    },
  );
};

export const usePayInvoice = (invoiceId?: string) => {
  const queryClient = useQueryClient();

  return useMutation<
    Invoice,
    Error,
    { paymentMethodId?: string; shouldSendEmail?: boolean }
  >(queryUpdate(`/v2/invoices/${invoiceId}/charge`), {
    onSuccess: (invoice) => {
      queryClient.setQueryData(['invoices', invoice.id], invoice);
    },
  });
};
